import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const EarthModel = ({ latitude, longitude, date }) => {
  const mountRef = useRef(null);
  
  // 🎥 核心引用 (用于在 Effect 之间共享实例)
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const earthGroupRef = useRef(null); // 控制地球旋转
  const markerRef = useRef(null);
  const sunGroupRef = useRef(null); 
  const sunLightRef = useRef(null);
  const controlsRef = useRef(null);
  
  // 🎮 开关引用
  const axisRef = useRef(null);
  const equatorRef = useRef(null);
  const starFieldRef = useRef(null); 

  // 🎛️ UI 状态
  const [showAxis, setShowAxis] = useState(false);
  const [showEquator, setShowEquator] = useState(false);
  const [showStars, setShowStars] = useState(true); 
  
  // 🔧 参数
  const DECLINATION_OFFSET = 4.9;
  const TIME_OFFSET = -1; 

  // 🏗️ 1. 初始化场景 (只运行一次)
  useEffect(() => {
    const mount = mountRef.current;
    const width = mount.clientWidth;
    const height = mount.clientHeight;

    // --- 场景与相机 ---
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 1000);
    const tilt = THREE.MathUtils.degToRad(23.5);
    camera.up.set(-Math.sin(tilt), Math.cos(tilt), 0); // 设置相机的"上"方向符合地轴倾角
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // --- 材质加载 (只加载一次) ---
    const isMobile = window.innerWidth < 768; 
    const suffix = '_4k.webp'; 
    const loader = new THREE.TextureLoader();
    
    // --- 物理组 (地轴倾斜容器) ---
    const physicsGroup = new THREE.Group();
    physicsGroup.rotation.z = THREE.MathUtils.degToRad(23.5); 
    scene.add(physicsGroup);

    // --- 星空 ---
    const starsGeometry = new THREE.BufferGeometry();
    const starCount = 15000; 
    const posArray = new Float32Array(starCount * 3);
    for(let i = 0; i < starCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 1800; 
    }
    starsGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const starsMaterial = new THREE.PointsMaterial({
      size: 0.6, color: '#ffffff', transparent: true, opacity: 0.9, sizeAttenuation: true, fog: false
    });
    const starField = new THREE.Points(starsGeometry, starsMaterial);
    starFieldRef.current = starField;
    scene.add(starField);

    // --- 辅助线 ---
    const axisGeometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, -12, 0), new THREE.Vector3(0, 12, 0)]);
    const axisLine = new THREE.Line(axisGeometry, new THREE.LineBasicMaterial({ color: 0xffd700, transparent: true, opacity: 0.6 }));
    axisRef.current = axisLine;
    physicsGroup.add(axisLine);

    const equatorGeometry = new THREE.RingGeometry(5.3, 5.35, 128);
    const equator = new THREE.Mesh(equatorGeometry, new THREE.MeshBasicMaterial({ color: 0x00ffff, side: THREE.DoubleSide, transparent: true, opacity: 0.2 }));
    equator.rotation.x = Math.PI / 2;
    equatorRef.current = equator;
    physicsGroup.add(equator);

    // --- 太阳 ---
    const sunGroup = new THREE.Group();
    sunGroupRef.current = sunGroup;
    physicsGroup.add(sunGroup);
    const sunLight = new THREE.DirectionalLight(0xffffff, 2.2); 
    sunLightRef.current = sunLight;
    sunGroup.add(sunLight);

    // --- 地球 (包含标记的容器) ---
    const earthGroup = new THREE.Group(); // 用于控制自转
    earthGroupRef.current = earthGroup;
    physicsGroup.add(earthGroup);

    const material = new THREE.MeshPhongMaterial({
      map: loader.load(`/earth_day${suffix}`),     
      bumpMap: loader.load(`/earth_bump${suffix}`), 
      bumpScale: 0.05,
      specular: new THREE.Color('#333'),
      shininess: 15,
      emissive: new THREE.Color(0xffff88),
      emissiveMap: loader.load(`/earth_night${suffix}`), 
      emissiveIntensity: 0.2, 
    });
    const earthMesh = new THREE.Mesh(new THREE.SphereGeometry(5, 64, 64), material);
    earthGroup.add(earthMesh);

    // --- 标记 ---
    const marker = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0xff4444, toneMapped: false })
    );
    markerRef.current = marker;
    // 注意：标记直接加在 earthGroup 上，随地球旋转
    earthGroup.add(marker);

    // --- 环境光 ---
    scene.add(new THREE.AmbientLight(0xffffff, 0.1)); 

    // --- 控制器 ---
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = true;
    controls.minDistance = 10;
    controls.maxDistance = 60;
    controlsRef.current = controls;

    // --- 动画循环 ---
    const clock = new THREE.Clock();
    let animationFrameId;

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      
      if (markerRef.current) {
        const time = clock.getElapsedTime();
        const scale = 1 + Math.sin(time * 2.5) * 0.4; 
        markerRef.current.scale.set(scale, scale, scale);
      }
      
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // --- 窗口 Resize 监听 ---
    const handleResize = () => {
        if (!mountRef.current) return;
        const w = mountRef.current.clientWidth;
        const h = mountRef.current.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // --- 清理函数 ---
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      mount.removeChild(renderer.domElement);
      
      // 内存清理建议
      renderer.dispose();
      material.map?.dispose();
      material.bumpMap?.dispose();
      material.emissiveMap?.dispose();
      material.dispose();
      earthMesh.geometry.dispose();
    };
  }, []); // 依赖为空，只运行一次

  // 🔄 2. 更新位置与时间 (当 props 变化时运行)
  useEffect(() => {
    if (!earthGroupRef.current || !markerRef.current || !cameraRef.current || !controlsRef.current) return;

    // 计算弧度
    const lonRad = THREE.MathUtils.degToRad(longitude);
    const latRad = THREE.MathUtils.degToRad(latitude);

    // 1. 设置地球旋转 (抵消经度，使 0 度经线正对视野中心，再根据输入旋转)
    // 之前逻辑是 earth.rotation.y = -Math.PI / 2 - lonRad;
    earthGroupRef.current.rotation.y = -Math.PI / 2 - lonRad;

    // 2. 更新标记位置 (相对于地球坐标系)
    // 你的标记计算逻辑是基于球坐标系的，这里保持不变，但要明确它是相对于 earthGroup 的
    // 由于 earthGroup 已经旋转了 lonRad，标记其实只需要根据 latRad 定位在"地球表面"的正确高度
    // 但为了让标记跟随地球转，我们需要重新思考坐标转换。
    // 最简单的方法：标记位置固定在 lat/lon 对应的三维空间点，而 earthGroup 负责旋转。
    
    // 这里采用更稳健的做法：
    // 地球自转通过 earthGroup.rotation.y 处理了经度变化。
    // 标记的位置如果也是基于 (lat, lon) 计算的静态坐标，那么它会随着 earthGroup 一起转。
    // 但是！如果你的 lon 变了，earthGroup 转了，标记如果也是用 lon 计算位置，那就会转两次 (Double Rotation)。
    
    // 修正：标记在 earthGroup 内部，它的位置应该只取决于 "0度经线" 的相对位置，或者我们让 earthGroup 转，标记永远设为 (lat, 0) ? 
    // 不，正确的做法是：earthGroup 转动负责将目标经度转到正面，而标记应该准确地打在那个经纬度上。
    
    // 实际上，更简单的做法是：
    // 1. EarthGroup 旋转 y = 0。
    // 2. 标记根据 lat/lon 计算位置。
    // 3. 整个 EarthGroup 旋转 -lonRad 让该经度面向相机？
    // 让我们沿用你之前的逻辑，那是能跑通的：
    
    const r = 5.02;
    // 这里 lonRad 在之前的代码中既用于旋转地球，也用于计算 marker 位置
    // marker.position.set(..., cos(lonRad), ..., sin(lonRad))
    // 如果 marker 是 earth 的子元素，且 earth 已经旋转了 -lonRad，那么 marker 的 lon 应该是 0 吗？
    // **重点**：如果 earth.rotation.y = -lonRad，那么地球贴图转动了。
    // 如果 marker 是子元素，它也跟着转了。
    // 如果 marker 的 position 计算公式里还包含 lonRad，那它的位置是相对于"已经转过的地球"再转一次。
    // **结论**：为了让标记准确对应贴图上的地理位置，计算 marker 位置时应该使用 "0" 作为经度偏移，或者是绝对经纬度计算，但这取决于贴图的 UV 映射起始点。
    
    // 假设你的代码之前是准确对齐的（通常贴图中心是经度0或180），我们保持你的计算公式不变，
    // 因为这通常意味着你是计算了世界坐标然后把 marker 放进去。
    // 等等，你的 marker 是 add(earth) 的。
    // 所以 marker 的坐标是局部坐标。
    
    // 让我们保持原样，因为你的原代码逻辑是：
    // earth.rotation.y = ... - lonRad
    // marker.position 包含 lonRad
    // 这在 ThreeJS 中会让 marker 始终指向"世界空间"的同一个方向（对着相机），而地球在下面转？
    // 或者是 marker 钉在地球上？
    
    // 如果 marker 包含 lonRad 计算，且 earth 旋转包含 -lonRad，两者抵消，marker 在世界坐标系中看起来是静止的（除了纬度变化），而地球纹理在移动。
    // 这意味着相机看到的是 marker 始终在中间，地球在转。这是符合逻辑的。
    
    markerRef.current.position.set(
      r * Math.cos(latRad) * Math.cos(lonRad),
      r * Math.sin(latRad),
      -r * Math.cos(latRad) * Math.sin(lonRad)
    );

    // 3. 相机跟随逻辑
    // 你之前的逻辑：计算 marker 世界坐标，然后把相机放到那个方向
    const target = new THREE.Vector3();
    markerRef.current.getWorldPosition(target);
    
    const isMobile = window.innerWidth < 768; 
    const distance = isMobile ? 35 : 22; 
    
    // 平滑移动相机（可选，如果想要瞬间跳跃就直接 copy）
    // cameraRef.current.position.lerp(target.normalize().multiplyScalar(distance), 0.1); 
    cameraRef.current.position.copy(target.normalize().multiplyScalar(distance));
    cameraRef.current.lookAt(0,0,0);
    
    // 确保 Controls 不会乱飘
    controlsRef.current.update();

  }, [latitude, longitude]); // 只有经纬度变化时才更新位置

  // 🌞 3. 独立更新太阳状态 (性能消耗极低)
  useEffect(() => {
    if (!sunGroupRef.current || !sunLightRef.current) return;
    
    // 提取的辅助函数逻辑
    const getSolarDeclination = (currentDate) => {
      const start = new Date(currentDate.getFullYear(), 0, 0);
      const diff = currentDate - start;
      const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
      return -23.44 * Math.cos(THREE.MathUtils.degToRad(360 / 365 * (dayOfYear + 10)));
    };

    const localHours = date.getHours() + date.getMinutes() / 60;
    const calibratedHours = localHours + TIME_OFFSET;
    const sunRotation = -(calibratedHours - 12) * (Math.PI / 12);
    sunGroupRef.current.rotation.y = sunRotation;

    const baseDeclination = getSolarDeclination(date);
    const finalDeclination = baseDeclination + DECLINATION_OFFSET;
    const sunDistance = 50;
    const phi = THREE.MathUtils.degToRad(finalDeclination); 
    const sunY = sunDistance * Math.sin(phi); 
    const sunOrbitRadius = sunDistance * Math.cos(phi); 

    sunLightRef.current.position.set(0, sunY, sunOrbitRadius);
    
  }, [date, DECLINATION_OFFSET, TIME_OFFSET]);

  // 💡 4. UI 开关响应
  useEffect(() => {
    if (axisRef.current) axisRef.current.visible = showAxis;
    if (equatorRef.current) equatorRef.current.visible = showEquator;
    if (starFieldRef.current) starFieldRef.current.visible = showStars;
  }, [showAxis, showEquator, showStars]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />

      {/* UI 面板保持不变 */}
      <div style={{
        position: 'absolute', top: '20px', right: '20px', width: 'auto', 
        padding: '12px 16px', background: 'rgba(10, 10, 10, 0.6)', 
        backdropFilter: 'blur(10px)', borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)', color: '#eee',
        fontFamily: 'system-ui, sans-serif', fontSize: '12px',
        display: 'flex', flexDirection: 'column', gap: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
      }}>
        <div style={{ fontWeight: 600, color: '#aaa', letterSpacing: '1px', fontSize: '10px', marginBottom: '2px' }}>显示选项</div>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
          <input type="checkbox" checked={showAxis} onChange={(e) => setShowAxis(e.target.checked)} style={{ accentColor: '#ffd700' }} />
          <span>地轴</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
          <input type="checkbox" checked={showEquator} onChange={(e) => setShowEquator(e.target.checked)} style={{ accentColor: '#00ffff' }} />
          <span>赤道</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
          <input type="checkbox" checked={showStars} onChange={(e) => setShowStars(e.target.checked)} style={{ accentColor: '#ffffff' }} />
          <span>星空</span>
        </label>
      </div>
    </div>
  );
};

export default EarthModel;